import { Store } from '@ngxs/store';
import { ProductsState } from 'src/app/shared/state/ProductsState';
import { AssetManagementState } from '../../../../shared/state/AssetManagementState';
import { Limit } from '../Limit';
import { MRT } from '../MRT';
import { ActionType, SpecLine, SpecificationType, getDispositionType, getActionType } from '../SpecLine';
import { DispositionType } from '../SpecLine';
import { UpdateLimit } from './UpdateLimit';
import { TranslationService } from '../../../../shared/translations/services/translation.service';

export interface UpdateSpecificationLine {
  id: string;
  type: number;
  label: string;
  specCode?: string;
  specDescription?: string;
  makes: string[];
  series: string[];
  sizes: string[];
  applicationDefinition: string;
  axles: string[];
  treads: string[];
  ntr: UpdateActionDetails;
  age: UpdateActionDetails;
  repair: UpdateActionDetails;
  limits: UpdateLimit[];
  exceedMichelinSpec: boolean;
  defaultProductSpec: boolean;
}

export interface UpdateActionDetails {
  limit: number;
  action: string;
  disposition?: string;
  disposalCode?: string;
  application?: string;
  axle?: string;
  treads?: string[];
}

export function mapToUpdateSpecificationLine(
  specificationLine: SpecLine,
  jsonFormValue: any,
  store: Store,
  translationService: TranslationService
) {
  const limits: UpdateLimit[] = specificationLine.limits.map((limit, index) => {
    const formLimit = index === 0 ? jsonFormValue.limit0 : jsonFormValue.limit1;
    return fillLimit(limit, formLimit);
  });
  const updateSpecificationLine: UpdateSpecificationLine = {
    id: specificationLine.id,
    type: specificationLine.type,
    label: jsonFormValue.label,
    specCode: jsonFormValue.specCode,
    specDescription: jsonFormValue.specDescription,
    makes: resolveDefinitionField(
      jsonFormValue.definition.makes.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).makes.length
    ),
    series: resolveDefinitionField(
      jsonFormValue.definition.series.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).series.length
    ),
    sizes: resolveDefinitionField(
      jsonFormValue.definition.sizes.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).sizes.length
    ),
    applicationDefinition: translationService.translateToEnglish(jsonFormValue.definition.applicationDefinition),
    axles: resolveDefinitionField(
      jsonFormValue.definition.axles.map((x) => translationService.translateToEnglish(x)),
      store.selectSnapshot(ProductsState.products).axles.length
    ),
    treads:
      specificationLine.type === SpecificationType.REPAIR_RETREAD
        ? resolveDefinitionField(
            jsonFormValue.definition.treads.map((x) => translationService.translateToEnglish(x)),
            store.selectSnapshot(ProductsState.products).treads.length
          )
        : null,
    ntr: mapActionDetails(jsonFormValue.ntr, translationService),
    age: mapActionDetails(jsonFormValue.age, translationService),
    repair: mapActionDetails(jsonFormValue.limit0, translationService),
    limits,
    exceedMichelinSpec: currentSpecExceedsMichelinSpec(jsonFormValue, specificationLine.limits.length, store),
    defaultProductSpec: specificationLine.defaultSpec
  };
  return updateSpecificationLine;
}

export function resolveDefinitionField(values: string[], total: number) {
  return values.length >= total ? ['All'] : values;
}

export function mapActionDetails(formActionDetails: any, translationService: TranslationService): UpdateActionDetails {
  const isActionDowngrade = getActionType(formActionDetails.action) === getActionType(ActionType.DOWNGRADE);
  const isDispositionReturn = getDispositionType(formActionDetails.disposition) === getDispositionType(DispositionType.RETURN);
  return {
    limit: getNullSafeValue(formActionDetails.limit),
    action: translationService.translateToEnglish(getActionType(formActionDetails.action)),
    disposition: isActionDowngrade ? null : translationService.translateToEnglish(getDispositionType(formActionDetails.disposition)),
    disposalCode: isActionDowngrade || isDispositionReturn ? null : formActionDetails.disposalCode,
    application: !isActionDowngrade ? null : translationService.translateToEnglish(formActionDetails.application),
    axle: !isActionDowngrade ? null : translationService.translateToEnglish(formActionDetails.axle),
    treads: !isActionDowngrade ? [] : formActionDetails.treads.map((x) => translationService.translateToEnglish(x))
  };
}

export function fillLimit(limit: Limit, formLimit: any): UpdateLimit {
  return {
    sectionNumber: limit.sectionNumber,
    totalRepairLimits: {
      totalRepairs: getNullSafeValue(formLimit.totalRepairs),
      nailRepairs: getNullSafeValue(formLimit.totalNail),
      rnailCrownRepairs: getNullSafeValue(formLimit.totalRnailCrown),
      rnailShoulder: {
        ...limit.totalRepairLimits.rnailShoulder,
        repairs: getNullSafeValue(formLimit.totalRnailShoulder),
        shoulder: {
          count: getNullSafeValue(formLimit.totalRnailShoulder),
          size: formLimit.rNailShoulderSize
        }
      },
      section: {
        repairs: getNullSafeValue(formLimit.totalSection),
        crown: {
          count: getNullSafeValue(formLimit.totalCrownSection),
          size: formLimit.maxCrownSize
        },
        shoulder: {
          count: getNullSafeValue(formLimit.totalShoulderSection),
          size: formLimit.maxShoulderSize
        },
        sidewall: {
          count: getNullSafeValue(formLimit.totalSidewallSection),
          size: formLimit.maxSidewallSize
        }
      }
    },
    otherRepairLimits: {
      spot: getNullSafeValue(formLimit.spot),
      bead: getNullSafeValue(formLimit.bead),
      liner: getNullSafeValue(formLimit.liner),
      brake: getNullSafeValue(formLimit.brakeSkid)
    }
  };
}

function getNullSafeValue(v: number) {
  if (!v) {
    return 0;
  }
  return v;
}

export function currentSpecExceedsMichelinSpec(specForm: any, limits: number, store: Store) {
  store.selectSnapshot(AssetManagementState.mrtSpecifications);
  const mrt = store.selectSnapshot(AssetManagementState.mrtSpecifications).get(specForm.definition.applicationDefinition);
  if (!mrt) {
    return false;
  }
  const ntrLimit = specForm.ntr.limit;
  const ageLimit = specForm.age.limit;
  const limit0 = specForm.limit0;
  const limit1 = specForm.limit1;
  const limit0Exceeds = limitExceedsChecker(limit0, mrt);
  let limit1Exceeds = false;
  if (limits === 2) {
    limit1Exceeds = limitExceedsChecker(limit1, mrt);
  }
  return (mrt.ntr ? ntrLimit > mrt.ntr : false) || (mrt.age ? ageLimit > mrt.age : false) || limit0Exceeds || limit1Exceeds;
}
function limitExceedsChecker(limit: any, mrt: MRT) {
  return (
    limit.totalRepairs > mrt.totalCombined ||
    limit.totalNail > mrt.nailRnail ||
    limit.totalRnailCrown > mrt.nailRnail ||
    limit.totalRnailShoulder > mrt.nailRnail ||
    limit.totalSection > mrt.totalSection ||
    limit.spot > mrt.spot ||
    limit.bead > mrt.bead ||
    limit.liner > mrt.liner
  );
}
